import { useTranslation } from "react-i18next";
import { Typography } from "@akuna/ui";
import { ProductSearch, type PdvItem } from "@/components/erp/pos/ProductSearch";
import { formatStayCurrency } from "./stayFormat";
import { SectionBoundary } from "./SectionBoundary";
import { useAddStayComandaLine, useRemoveStayComandaLine, useStayComanda, useUpdateStayComandaLine } from "./useStayComanda";
import { ComandaLineRow } from "../reservation/ComandaLineRow";
import { ComandaSectionLayout } from "../reservation/ComandaSectionLayout";

export interface StayComandaSectionProps {
  reservationId: string;
  readOnly?: boolean;
}

export function StayComandaSection({ reservationId, readOnly = false }: StayComandaSectionProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const comanda = useStayComanda(reservationId, readOnly);
  const addLine = useAddStayComandaLine();
  const updateLine = useUpdateStayComandaLine();
  const removeLine = useRemoveStayComandaLine();

  if (comanda.isLoading || comanda.isError) {
    return <SectionBoundary isLoading={comanda.isLoading} isError={comanda.isError} skeletonClassName="h-28 rounded-2xl" />;
  }

  const language = i18n.resolvedLanguage ?? i18n.language ?? "pt-BR";
  const lines = comanda.data?.lines ?? [];

  return (
    <ComandaSectionLayout
      title={t("panel.sectionComanda")}
      search={readOnly ? undefined : <ProductSearch onSelectProduct={(product: PdvItem) => addLine.mutate({ reservationId, saleId: comanda.data?.saleId ?? "", itemName: product.name, unitPrice: product.sale_price ?? 0 })} popoverPortalled={false} />}
      totalLabel={t("panel.comandaTotal")}
      total={formatStayCurrency(comanda.data?.total ?? 0, language)}
    >
      {!comanda.data ? (
        <Typography variant="body-sm" className="text-muted-foreground">{t("panel.comandaMissing")}</Typography>
      ) : (
        <div>
          <ul>
            {lines.map((line) => (
              <ComandaLineRow key={line.id} line={line} onChange={readOnly ? undefined : (patch) => updateLine.mutate({ reservationId, lineId: line.id, quantity: patch.quantity ?? line.quantity, unitPrice: patch.unitPrice ?? line.unitPrice })} onRemove={readOnly ? undefined : () => removeLine.mutate({ reservationId, lineId: line.id })} />
            ))}
          </ul>
        </div>
      )}
    </ComandaSectionLayout>
  );
}
